import React from "react";
import Select from "../Select";
import { trackAmplitudeEvent } from "../../amplitude";
import Icon from "../Icon";
import Modal from "../Modal";
import ProSubscribeModal from "../ProSubscribeModal";
import { ModeSelect, NumberField, SkillSelect, modeAfterSkillChange } from "./PlayStyleFields";
import { FREE_PLAN_LIMIT, resolvePlayStyle } from "./todaysPlanService";

const ROUND_LENGTH_OPTIONS = [1, 5, 10, 20, 50, 100];

const hideModal = (id) => $(`#${id}`).modal("hide");

const LEARNING_METHODS = [
  {
    description: "Strengthens recognition: just pick the right answer for simple, fast practice and light review.",
    tip: "Great for beginners.",
    title: "Multiple Choice (Easy)"
  },
  {
    description: "Strengthens active recall: recall the answer in your head for high-volume review and consistent daily habit-building.",
    tip: "Perfect for anyone who wants maximum review volume in minimal time.",
    title: "Flashcards (Sustainable)"
  },
  {
    description: "Strengthens active recall: type the answers for short, high-impact study sessions.",
    tip: "Perfect for anyone who wants maximum retention with minimal time.",
    title: "Typing Answers (Efficient)"
  },
  {
    description: "Supports deep processing and precision: transcribe whole sentences for slower, focused learning sessions.",
    tip: "For learners who want to develop precision over time.",
    title: "Transcribing Sentences (Very Hard)"
  }
];

export function LearningMethodsModal({ onHidden }) {
  const modalRef = React.useRef(null);

  React.useEffect(() => {
    trackAmplitudeEvent("learning_methods_bottom_sheet_visible");
  }, []);

  return (
    <Modal onHidden={onHidden} ref={modalRef} size="small" title="Learning methods">
      <div className="tw-flex tw-flex-col tw-gap-4" data-testid="learning-methods">
        {LEARNING_METHODS.map(({ description, tip, title }) => (
          <div key={title}>
            <h5 style={{ fontWeight: 600 }}>{title}</h5>
            <p>{description}</p>
            <div className="alert alert-success" style={{ marginBottom: 0 }}><small>{tip}</small></div>
          </div>
        ))}
        <button className="btn btn-success btn-block joystix" onClick={() => modalRef.current.hide()}>OK</button>
      </div>
    </Modal>
  );
}

export function CollectionSettingsModal({ availability, collection, collectionConfig, onCountChange, onHidden, onPlayOptionsChange, row }) {
  const [isLearningMethodsVisible, setIsLearningMethodsVisible] = React.useState(false);
  const isReview = row.type === "review";
  const key = `${row.collectionId}-${row.type}`;
  const isListeningAvailable = availability.isSystemTtsAvailable || collection.ttsAvailable;
  // A skill set on a device that has it shows as what this browser plays, without changing what is saved.
  const { mode, skill } = resolvePlayStyle(
    isReview ? collectionConfig.reviewSkill : collectionConfig.newWordsSkill,
    isReview ? collectionConfig.reviewMode : collectionConfig.newWordsMode,
    { listening: isListeningAvailable, speaking: availability.isSpeakingAvailable }
  );
  const roundLength = isReview ? collectionConfig.reviewRoundLength : collectionConfig.newWordsRoundLength;
  const isTranslateAvailable = availability.isTranslateReviewsEnabled && isReview;

  const changeSkill = (newSkill) => onPlayOptionsChange(isReview ? { reviewSkill: newSkill } : { newWordsSkill: newSkill });

  return (
    <Modal onHidden={onHidden} title={row.name}>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <NumberField
          label={isReview ? "Reviews Today" : "New words today"}
          min={0}
          onCommit={onCountChange}
          testId={`per-day-${key}`}
          value={row.perDay}
        />
        <NumberField
          label="Round Length"
          min={1}
          onCommit={(value) => onPlayOptionsChange(isReview ? { reviewRoundLength: value } : { newWordsRoundLength: value })}
          testId={`round-length-${key}`}
          value={roundLength}
        />
        <SkillSelect
          isListeningAvailable={isListeningAvailable}
          isSpeakingAvailable={availability.isSpeakingAvailable}
          onChange={changeSkill}
          onInfoClick={() => setIsLearningMethodsVisible(true)}
          skill={skill}
          testId={`skill-${key}`}
        />
        <ModeSelect
          isTranslateAvailable={isTranslateAvailable}
          mode={mode}
          onChange={(newMode) => onPlayOptionsChange(isReview ? { reviewMode: newMode } : { newWordsMode: newMode })}
          skill={skill}
          testId={`mode-${key}`}
        />
      </div>
      {isLearningMethodsVisible && <LearningMethodsModal onHidden={() => setIsLearningMethodsVisible(false)} />}
    </Modal>
  );
}

const isTotalInvalid = (value) => {
  const parsed = parseInt(value, 10);
  return value === "" || isNaN(parsed) || parsed < 0;
};

const TotalField = ({ label, onChange, testId, value }) => (
  <div>
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
      <label className="text-secondary" style={{ margin: 0 }}>{label}</label>
      <input className="form-control" data-testid={testId} onChange={(e) => onChange(e.target.value)} style={{ textAlign: "center", width: 72 }} type="number" value={value} />
    </div>
    {isTotalInvalid(value) && <div className="text-danger text-right"><small>Enter a number greater than 0</small></div>}
  </div>
);

export function DailyTotalsModal({ defaultMaxReviewsPerCollectionPerDay, newWordsRows, onHidden, onOpenReviewSettings, onSave, reviewRows }) {
  const plannedTotal = (rows) => rows.filter((row) => row.enabled).reduce((s, row) => s + row.perDay, 0);
  const plannedReviews = plannedTotal(reviewRows);
  const plannedNewWords = plannedTotal(newWordsRows);
  const [reviewsValue, setReviewsValue] = React.useState(String(plannedReviews));
  const [newWordsValue, setNewWordsValue] = React.useState(String(plannedNewWords));
  const isInvalid = isTotalInvalid(reviewsValue) || isTotalInvalid(newWordsValue);
  const isReviewLimitSet = defaultMaxReviewsPerCollectionPerDay != null && defaultMaxReviewsPerCollectionPerDay >= 0;

  const save = () => {
    const reviews = parseInt(reviewsValue, 10);
    const newWords = parseInt(newWordsValue, 10);
    onSave({
      newWords: newWords !== plannedNewWords ? newWords : undefined,
      reviews: reviews !== plannedReviews ? reviews : undefined
    });
    hideModal("todays-plan-daily-totals-modal");
  };

  return (
    <Modal id="todays-plan-daily-totals-modal" onHidden={onHidden} title="Daily totals">
      <div className="tw-flex tw-flex-col tw-gap-4">
        <p className="text-secondary">How many reviews and new words to plan each day, spread across your collections.</p>
        <TotalField label="Reviews" onChange={setReviewsValue} testId="daily-total-reviews" value={reviewsValue} />
        <TotalField label="New words" onChange={setNewWordsValue} testId="daily-total-new-words" value={newWordsValue} />
        {isReviewLimitSet && (
          <div className="alert alert-success" data-testid="review-limit-notice" style={{ marginBottom: 0 }}>
            <small>
              Reviews limited to {defaultMaxReviewsPerCollectionPerDay} per collection, you can edit this limit{" "}
              <a onClick={() => { hideModal("todays-plan-daily-totals-modal"); onOpenReviewSettings(); }} role="button">here</a>
            </small>
          </div>
        )}
        <button className="btn btn-success btn-block joystix" data-testid="save-daily-totals" disabled={isInvalid} onClick={save}>Save</button>
      </div>
    </Modal>
  );
}

const APPLY_TO_OPTIONS = [["new_words", "New words"], ["review", "Reviews"], ["both", "Both"]];

export function PlayStyleModal({ availability, collections, config, draft, onApply, onDraftChange, onHidden }) {
  const first = config.collections[0] || {};
  const isListeningAvailable = availability.isSystemTtsAvailable || collections.some((c) => c.ttsAvailable);
  const currentStyleFor = (target) => {
    const style = target === "new_words"
      ? { mode: first.newWordsMode, roundLength: first.newWordsRoundLength, skill: first.newWordsSkill }
      : { mode: first.reviewMode, roundLength: first.reviewRoundLength, skill: first.reviewSkill };
    const available = { listening: isListeningAvailable, speaking: availability.isSpeakingAvailable };
    return { ...style, ...resolvePlayStyle(style.skill || "vocabulary", style.mode || "multiple_choice", available) };
  };
  // Like the app's sheet, which stays mounted: the last Apply to choice and unapplied edits are still there next time.
  const initialTarget = draft ? draft.appliesTo : "both";
  const [appliesTo, setAppliesTo] = React.useState(initialTarget);
  const [isDescriptionVisible, setIsDescriptionVisible] = React.useState(false);
  const [skill, setSkill] = React.useState(draft ? draft.skill : currentStyleFor(initialTarget).skill || "vocabulary");
  const [mode, setMode] = React.useState(draft ? draft.mode : currentStyleFor(initialTarget).mode || "multiple_choice");
  const [roundLength, setRoundLength] = React.useState(draft ? draft.roundLength : currentStyleFor(initialTarget).roundLength || 10);

  React.useEffect(() => {
    onDraftChange({ appliesTo, mode, roundLength, skill });
  }, [appliesTo, mode, roundLength, skill]);
  const isTranslateAvailable = availability.isTranslateReviewsEnabled && appliesTo === "review";
  const roundLengths = ROUND_LENGTH_OPTIONS.includes(roundLength) ? ROUND_LENGTH_OPTIONS : [...ROUND_LENGTH_OPTIONS, roundLength].sort((a, b) => a - b);

  const changeSkill = (newSkill) => {
    setSkill(newSkill);
    setMode(modeAfterSkillChange(newSkill, mode));
  };

  const changeAppliesTo = (target) => {
    const current = currentStyleFor(target);
    setAppliesTo(target);
    setSkill(current.skill || "vocabulary");
    setMode(current.mode || "multiple_choice");
    setRoundLength(current.roundLength || 10);
  };

  const apply = () => {
    onApply({ appliesTo, mode, roundLength, skill });
    hideModal("todays-plan-play-style-modal");
  };

  return (
    <Modal id="todays-plan-play-style-modal" onHidden={onHidden} title="Gameplay Settings">
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-justify-end" style={{ marginTop: -8 }}>
          <button aria-label="gameplay settings info" className="btn btn-link btn-xs" onClick={() => setIsDescriptionVisible(!isDescriptionVisible)} type="button">
            <Icon name="info-circle" type="fa" />
          </button>
        </div>
        {isDescriptionVisible && (
          <p className="alert alert-info" data-testid="gameplay-settings-description" style={{ marginBottom: 0 }}>
            Apply gameplay settings to multiple collections in your plan at once. This overwrites their current settings, but you can still customize a single collection, and its new words and reviews separately, from your plan.
          </p>
        )}
        <div className="btn-group btn-group-justified" role="group" aria-label="Apply to">
          {APPLY_TO_OPTIONS.map(([value, label]) => (
            <a className={`btn btn-default ${appliesTo === value ? "active" : ""}`} key={value} onClick={() => changeAppliesTo(value)} role="button">{label}</a>
          ))}
        </div>
        <SkillSelect
          isListeningAvailable={isListeningAvailable}
          isSpeakingAvailable={availability.isSpeakingAvailable}
          onChange={changeSkill}
          skill={skill}
          testId="default-play-style-skill"
        />
        <ModeSelect isTranslateAvailable={isTranslateAvailable} mode={mode} onChange={setMode} skill={skill} testId="default-play-style-mode" />
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <label className="text-secondary" style={{ margin: 0 }}>Round Length</label>
          <Select data-testid="default-round-length-select" onChange={(e) => setRoundLength(parseInt(e.target.value, 10))} style={{ width: "auto" }} value={roundLength}>
            {roundLengths.map((value) => <option key={value} value={value}>{value}</option>)}
          </Select>
        </div>
        <button className="btn btn-success btn-block joystix" data-testid="apply-default-play-style" onClick={apply}>Apply Settings</button>
      </div>
    </Modal>
  );
}

export function InfoModal({ onHidden }) {
  return (
    <Modal onHidden={onHidden} size="small" title="Today's Plan">
      <p className="text-secondary">Stick with our smart recommendation or customize a plan that works for you.</p>
      <h5 className="joystix">Our Plan</h5>
      <p className="text-secondary">
        We structure your day for maximum learning efficiency: you'll tackle your due reviews first, followed by 10 new sentences per collection. This is tailored to your onboarding preferences and can be updated anytime.
      </p>
      <h5 className="joystix">Your Custom Plan</h5>
      <p className="text-secondary">Want more control? Open the plan settings to:</p>
      <ul className="text-secondary">
        <li>Adjust daily word counts and change collection order.</li>
        <li>Choose specific skills and game modes per collection.</li>
        <li>Dedicate your day entirely to reviews or focus solely on new words.</li>
      </ul>
    </Modal>
  );
}

export function DailyLimitModal({ onHidden }) {
  React.useEffect(() => {
    trackAmplitudeEvent("web_app_hit_daily_sentence_limit_modal", { modal_version: "todays_plan", sentence_limit: FREE_PLAN_LIMIT });
  }, []);

  return (
    <Modal onHidden={onHidden} size="small" title="Daily Limit Reached">
      <p className="text-center">You've played all {FREE_PLAN_LIMIT} free sentences for today!</p>
      <p className="text-center">Upgrade to Pro for <strong>unlimited daily sentences</strong> and much more.</p>
      <ProSubscribeModal
        buttonClass="btn btn-success btn-block btn-lg joystix"
        buttonText="Go Pro - Keep Playing!"
        onSubscribed={({ isPro }) => { if(isPro) window.location.reload(); }}
        paddleConfigUrl="/api/v1/users/paddle_config"
        showBenefits={true}
      />
      <p className="text-center text-secondary" style={{ marginBottom: 0, marginTop: 12 }}>Or come back tomorrow for {FREE_PLAN_LIMIT} more free sentences.</p>
    </Modal>
  );
}
